import { action, makeObservable, observable } from 'mobx';
import { RootStore } from './root';
import { WebAuthnChallengeData } from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { PublicKeyCredentialWithJSON } from '../types';

export enum WebAuthnError {
  NOT_SUPPORTED = 'WEBAUTHN_NOT_SUPPORTED',
  CHALLENGE_FAILED = 'WEBAUTHN_CHALLENGE_FAILED',
  AUTHENTICATION_FAILED = 'WEBAUTHN_AUTHENTICATION_FAILED',
  USER_CANCELLED = 'WEBAUTHN_USER_CANCELLED',
  UNKNOWN_ERROR = 'WEBAUTHN_UNKNOWN_ERROR',
}

export class WebAuthnStore {
  private readonly rootStore: RootStore;

  public isLoading: boolean = false;
  public error: string | null = null;
  private webAuthnAbortController: AbortController | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      isLoading: observable,
      error: observable,
      clearError: action.bound,
      clear: action.bound,
    });
  }

  /**
   * Regular flow to authenticate with WebAuthn
   */
  private async createWebAuthnChallenge(
    email?: string,
  ): Promise<WebAuthnChallengeData | null> {
    this.isLoading = true;
    this.clearError();

    try {
      const challengeResponse =
        await this.rootStore.apiStore.getWebauthnChallenge({
          loginId: { email },
        });
      return challengeResponse.webauthn || null;
    } catch (error) {
      // TODO: Handle WebAuthn challenge creation error properly
      console.error('WebAuthn challenge creation failed:', error);
      this.error = WebAuthnError.CHALLENGE_FAILED;
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  private parseCredentialRequestOptions(
    challengeData: WebAuthnChallengeData,
  ): PublicKeyCredentialRequestOptions {
    const { publicKey } = JSON.parse(
      challengeData.publicKeyCredentialRequestOptionsJson!,
    );

    return (
      PublicKeyCredential as unknown as PublicKeyCredentialWithJSON
    ).parseRequestOptionsFromJSON(publicKey);
  }

  private async requestCredentialFromBrowser(
    credentialRequestOptions: PublicKeyCredentialRequestOptions,
    useConditionalUI?: boolean,
  ): Promise<PublicKeyCredential | null> {
    const credential = (await navigator.credentials.get({
      publicKey: credentialRequestOptions,
      ...(useConditionalUI && {
        mediation: 'conditional' as CredentialMediationRequirement,
        signal: this.webAuthnAbortController?.signal,
      }),
    })) as PublicKeyCredential;

    return credential;
  }

  public async authenticateWithWebAuthn(email: string): Promise<{
    credential: PublicKeyCredential;
    challenge: WebAuthnChallengeData;
  }> {
    this.isLoading = true;
    this.clearError();

    try {
      // Step 1: Get challenge from server
      const challenge = await this.createWebAuthnChallenge(email);

      if (!challenge) {
        throw new Error(WebAuthnError.CHALLENGE_FAILED);
      }

      // Step 2: Parse the challenge data
      const credentialRequestOptions =
        this.parseCredentialRequestOptions(challenge);

      // Step 3: Request credential from browser
      const credential = await this.requestCredentialFromBrowser(
        credentialRequestOptions,
      );

      if (!credential) {
        throw new Error(WebAuthnError.AUTHENTICATION_FAILED);
      }

      return { credential, challenge };
    } catch (error) {
      // TODO: Handle WebAuthn authentication error properly
      console.error('WebAuthn authentication failed:', error);
      this.error = WebAuthnError.AUTHENTICATION_FAILED;
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Usernameless flow to authenticate with WebAuthn
   */
  private async isConditionalMediationAvailable(): Promise<boolean> {
    return PublicKeyCredential.isConditionalMediationAvailable();
  }

  public async authenticateWithUsernamelessWebAuthn(): Promise<{
    credential: PublicKeyCredential;
    challenge: WebAuthnChallengeData;
  } | null> {
    try {
      // Step 1: Check if conditional mediation is available
      const isConditionalMediationAvailable =
        await this.isConditionalMediationAvailable();
      if (!isConditionalMediationAvailable) {
        return null;
      }

      // Step 2: Get usernameless challenge from server
      const challenge = await this.createWebAuthnChallenge();
      if (!challenge) {
        return null;
      }

      // Step 3: Parse the challenge data
      const credentialRequestOptions =
        this.parseCredentialRequestOptions(challenge);

      // Step 4: Setup abort controller for conditional UI
      this.webAuthnAbortController = new AbortController();

      // Step 5: Request credential from browser with conditional UI
      const credential = await this.requestCredentialFromBrowser(
        credentialRequestOptions,
        true,
      );

      if (!credential) {
        return null;
      }

      return { credential, challenge };
    } catch (error) {
      // TODO: Handle usernameless WebAuthn authentication error properly
      console.error('Usernameless WebAuthn authentication failed:', error);
      this.error = WebAuthnError.AUTHENTICATION_FAILED;
      return null;
    }
  }

  public abortWebAuthnAuthentication(): void {
    if (this.webAuthnAbortController) {
      this.webAuthnAbortController.abort();
      this.webAuthnAbortController = null;
    }
  }

  /**
   * Clear any existing error
   */
  public clearError(): void {
    this.error = null;
  }

  /**
   * Clear all store state
   */
  public clear(): void {
    this.abortWebAuthnAuthentication();
    this.isLoading = false;
    this.error = null;
  }
}
